import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card, Badge, DataTable } from "../components/ui";

// Icone per tipo documento immobili
const getIconForType = (value) => {
  const v = parseInt(value);
  if (v === 100000012) return "📐"; // Planimetria
  if (v === 100000013) return "📋"; // Scheda tecnica immobile
  if (v === 100000014) return "✅"; // Dichiarazione conformità
  if (v === 100000015) return "⚙️"; // Documentazione impianti
  // Nuove categorie documentazione tecnica progetto
  if (v === 100000016) return "🏗️"; // Progetto architettonico
  if (v === 100000017) return "⚡"; // Impianto elettrico
  if (v === 100000018) return "🔧"; // Impianto meccanico
  if (v === 100000019) return "📊"; // Planimetrie strutturali
  if (v === 100000020) return "📁"; // Documentazione generale progetto
  if (v === 100000400) return "📄"; // Agibilità
  if (v === 100000401) return "🔑"; // Affitto/Locazione
  // Tipi personalizzati immobili
  return "🏠";
};

function DocumentiImmobiliPage() {
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [documenti, setDocumenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [tipiDocumentoImmobile, setTipiDocumentoImmobile] = useState([]);

  // Filtri
  const [filters, setFilters] = useState({
    rsaId: "",
    tipoDocumento: "",
    search: "",
  });

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const isDirezione = user?.ruolo === 100000002;
  const isDirettoreRsa = user?.ruolo === 100000003;
  const canManage = isAdmin || isRspp;

  // Carica lista RSA e tipi documento immobili
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Carica tipi documento e filtra quelli con ambito Immobili
        const tipiResult = await apiRequest(
          "/admin/dataverse/tipodocumento-options",
        );
        const tipiImmobili = (tipiResult.options || [])
          .filter((t) => t.ambito === "Immobili")
          .map((t) => ({
            value: t.value,
            label: t.label,
            icon: getIconForType(t.value),
          }));
        setTipiDocumentoImmobile(tipiImmobili);

        // Carica RSA se utente autorizzato
        if (canManage || isDirezione) {
          const rsaResult = await apiRequest("/rsa");
          setRsaList(rsaResult.value || []);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, [apiRequest, canManage, isDirezione]);

  // Carica documenti
  useEffect(() => {
    const fetchDocumenti = async () => {
      // Aspetta che i tipi siano caricati
      if (tipiDocumentoImmobile.length === 0) {
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams();

        if (filters.tipoDocumento) {
          // Filtro specifico per un tipo: invia solo quello
          params.append("tipoDocumento", filters.tipoDocumento);
        } else {
          // Nessun filtro specifico: invia tutti i tipi immobili
          const tipiImmobile = tipiDocumentoImmobile
            .map((t) => t.value)
            .join(",");
          params.append("tipiDocumento", tipiImmobile);
        }

        if (filters.rsaId) {
          params.append("rsaId", filters.rsaId);
        }
        if (filters.search) {
          params.append("search", filters.search);
        }

        const result = await apiRequest(`/documenti?${params.toString()}`);
        setDocumenti(result.value || []);
      } catch (error) {
        console.error("Error fetching documenti:", error);
        showToast("Errore nel caricamento dei documenti", "error");
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchDocumenti, 300);
    return () => clearTimeout(debounce);
  }, [apiRequest, filters, showToast, tipiDocumentoImmobile]);

  const handleDownload = async (doc) => {
    try {
      const response = await apiRequest(`/documenti/${doc.id}/download`, {
        responseType: "blob",
      });

      // Se la risposta è un URL, apri in nuova finestra
      if (typeof response === "string") {
        window.open(response, "_blank");
      } else if (response.downloadUrl) {
        window.open(response.downloadUrl, "_blank");
      } else {
        // Altrimenti, scarica il blob
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.download = doc.nome || "documento";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading:", error);
      showToast("Errore nel download del documento", "error");
    }
  };

  const handlePreview = async (doc) => {
    try {
      const result = await apiRequest(`/documenti/${doc.id}/preview`);
      // Il backend ritorna 'url' per SharePoint
      const previewUrl = result.previewUrl || result.url;
      if (previewUrl) {
        window.open(previewUrl, "_blank");
      } else {
        showToast(
          "Anteprima non disponibile per questo tipo di file",
          "warning",
        );
      }
    } catch (error) {
      console.error("Error getting preview:", error);
      showToast("Errore nel caricamento dell'anteprima", "error");
    }
  };

  // Helper per ottenere info tipo documento
  const getTipoInfo = (tipoValue) => {
    const tipo = tipiDocumentoImmobile.find((t) => t.value === tipoValue);
    return tipo || { label: "-", icon: "📄" };
  };

  // Helper per estrarre estensione file e info formato
  const getFileFormat = (row) => {
    // Prova a estrarre dall'URL SharePoint
    const url = row.urlSharepoint || "";
    const nome = row.nome || "";

    // Estrai estensione dall'URL (prima del ? se presente)
    let ext = "";
    const urlPath = url.split("?")[0];
    const urlMatch = urlPath.match(/\.([a-zA-Z0-9]+)$/i);
    if (urlMatch) {
      ext = urlMatch[1].toLowerCase();
    }

    // Se non trovata nell'URL, prova dal nome
    if (!ext) {
      const nomeMatch = nome.match(/\.([a-zA-Z0-9]+)$/i);
      if (nomeMatch) {
        ext = nomeMatch[1].toLowerCase();
      }
    }

    // Mappa estensioni a icone e colori
    const formatMap = {
      pdf: { icon: "📕", label: "PDF", color: "bg-red-100 text-red-700" },
      dwg: { icon: "📐", label: "DWG", color: "bg-blue-100 text-blue-700" },
      dxf: { icon: "📐", label: "DXF", color: "bg-blue-100 text-blue-700" },
      doc: { icon: "📘", label: "DOC", color: "bg-blue-100 text-blue-700" },
      docx: { icon: "📘", label: "DOCX", color: "bg-blue-100 text-blue-700" },
      xls: { icon: "📗", label: "XLS", color: "bg-green-100 text-green-700" },
      xlsx: { icon: "📗", label: "XLSX", color: "bg-green-100 text-green-700" },
      ppt: { icon: "📙", label: "PPT", color: "bg-orange-100 text-orange-700" },
      pptx: {
        icon: "📙",
        label: "PPTX",
        color: "bg-orange-100 text-orange-700",
      },
      jpg: { icon: "🖼️", label: "JPG", color: "bg-purple-100 text-purple-700" },
      jpeg: {
        icon: "🖼️",
        label: "JPEG",
        color: "bg-purple-100 text-purple-700",
      },
      png: { icon: "🖼️", label: "PNG", color: "bg-purple-100 text-purple-700" },
      svg: { icon: "🎨", label: "SVG", color: "bg-pink-100 text-pink-700" },
      zip: { icon: "📦", label: "ZIP", color: "bg-gray-100 text-gray-700" },
      rar: { icon: "📦", label: "RAR", color: "bg-gray-100 text-gray-700" },
      txt: { icon: "📄", label: "TXT", color: "bg-gray-100 text-gray-700" },
      csv: { icon: "📊", label: "CSV", color: "bg-green-100 text-green-700" },
    };

    return (
      formatMap[ext] || {
        icon: "📄",
        label: ext.toUpperCase() || "-",
        color: "bg-gray-100 text-gray-600",
      }
    );
  };

  const columns = [
    {
      key: "nome",
      label: "Documento",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-xl">{getIconForType(row.tipoDocumento)}</span>
          <div>
            <p className="font-medium text-gray-900">{row.nome}</p>
            {row.descrizione && (
              <p className="text-sm text-gray-500 truncate max-w-xs">
                {row.descrizione}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "tipoDocumento",
      label: "Tipo",
      render: (row) => {
        const tipo = getTipoInfo(row.tipoDocumento);
        return tipo.label !== "-" ? (
          <Badge variant="success">{tipo.label}</Badge>
        ) : (
          <span className="text-gray-400">-</span>
        );
      },
    },
    {
      key: "formato",
      label: "Formato",
      render: (row) => {
        const format = getFileFormat(row);
        return (
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${format.color}`}
          >
            {format.icon} {format.label}
          </span>
        );
      },
    },
    {
      key: "rsa",
      label: "RSA",
      render: (row) => row.rsa || "-",
    },
    {
      key: "immobile",
      label: "Immobile",
      render: (row) =>
        row.immobile ? (
          <span className="text-primary-600 font-medium">{row.immobile}</span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      key: "dataDocumento",
      label: "Data",
      render: (row) =>
        row.dataDocumento
          ? new Date(row.dataDocumento).toLocaleDateString("it-IT")
          : "-",
    },
    {
      key: "dataScadenza",
      label: "Scadenza",
      render: (row) => {
        if (!row.dataScadenza) return "-";
        const scadenza = new Date(row.dataScadenza);
        const oggi = new Date();
        const isScaduto = scadenza < oggi;
        const isInScadenza =
          scadenza <= new Date(oggi.getTime() + 30 * 24 * 60 * 60 * 1000);

        return (
          <span
            className={
              isScaduto
                ? "text-red-600 font-medium"
                : isInScadenza
                  ? "text-amber-600"
                  : ""
            }
          >
            {scadenza.toLocaleDateString("it-IT")}
            {isScaduto && " (Scaduto)"}
          </span>
        );
      },
    },
    {
      key: "azioni",
      label: "Azioni",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePreview(row)}
            title="Anteprima"
          >
            👁️
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDownload(row)}
            title="Download"
          >
            ⬇️
          </Button>
          {row.urlSharepoint && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(row.urlSharepoint, "_blank")}
              title="Apri in SharePoint"
            >
              📂
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Statistiche per tipo (usando lista dinamica)
  const statsByType = tipiDocumentoImmobile.map((tipo) => ({
    ...tipo,
    count: documenti.filter((d) => d.tipoDocumento === tipo.value).length,
  }));

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Documenti Immobili
          </h1>
          <p className="text-gray-600 mt-1">
            Planimetrie, schede tecniche, certificazioni e documentazione
            impianti
          </p>
        </div>
        {canManage && (
          <Button onClick={() => navigate("/admin/documenti")}>
            + Carica Documento
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {statsByType.map((tipo) => (
          <Card
            key={tipo.value}
            className={`cursor-pointer transition-all ${
              filters.tipoDocumento === tipo.value.toString()
                ? "ring-2 ring-primary-500"
                : "hover:shadow-md"
            }`}
            onClick={() =>
              setFilters((f) => ({
                ...f,
                tipoDocumento:
                  f.tipoDocumento === tipo.value.toString()
                    ? ""
                    : tipo.value.toString(),
              }))
            }
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{tipo.icon}</span>
              <div>
                <p className="text-2xl font-bold text-gray-900">{tipo.count}</p>
                <p className="text-sm text-gray-500">{tipo.label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filtri */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Cerca documento..."
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
            className="input flex-1 min-w-[200px]"
          />

          {(canManage || isDirezione) && (
            <select
              value={filters.rsaId}
              onChange={(e) =>
                setFilters((f) => ({ ...f, rsaId: e.target.value }))
              }
              className="input w-48"
            >
              <option value="">Tutte le RSA</option>
              {rsaList.map((rsa) => (
                <option key={rsa.id} value={rsa.id}>
                  {rsa.nome}
                </option>
              ))}
            </select>
          )}

          <select
            value={filters.tipoDocumento}
            onChange={(e) =>
              setFilters((f) => ({ ...f, tipoDocumento: e.target.value }))
            }
            className="input w-56"
          >
            <option value="">Tutti i tipi</option>
            {tipiDocumentoImmobile.map((tipo) => (
              <option key={tipo.value} value={tipo.value}>
                {tipo.icon} {tipo.label}
              </option>
            ))}
          </select>

          {(filters.search || filters.rsaId || filters.tipoDocumento) && (
            <Button
              variant="outline"
              onClick={() =>
                setFilters({ rsaId: "", tipoDocumento: "", search: "" })
              }
            >
              Pulisci filtri
            </Button>
          )}
        </div>
      </Card>

      {/* Tabella */}
      <Card>
        <DataTable
          data={documenti}
          columns={columns}
          loading={loading}
          searchable={false}
          emptyMessage="Nessun documento trovato"
          emptyIcon="📄"
        />
      </Card>
    </div>
  );
}

export default DocumentiImmobiliPage;
